import React from "react";
import { IconAlertCircle, IconLoader2, IconRefresh } from "@tabler/icons-react";
import {
    FaceSearchError,
    getFaceSearchErrorCopy,
    isFaceServerError,
} from "./face-search-errors";

type Props = {
    isSearching: boolean;
    error: FaceSearchError | null;
    onRetry: () => void;
    onClose: () => void;
    searchingMessage?: string;
    searchingDetail?: string;
};

const FaceSearchFeedback = ({
    isSearching,
    error,
    onRetry,
    onClose,
    searchingMessage = "Matching face...",
    searchingDetail = "Please wait while we search for service requests.",
}: Props) => {
    if (isSearching) {
        return (
            <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl bg-slate-50 px-6 py-10 text-center">
                <IconLoader2 size={36} className="mb-4 animate-spin text-blue-600" />
                <p className="text-base font-semibold text-gray-900">{searchingMessage}</p>
                <p className="mt-2 text-sm text-gray-500">{searchingDetail}</p>
            </div>
        );
    }

    if (!error) return null;

    const copy = getFaceSearchErrorCopy(error);
    const serverError = isFaceServerError(error);

    return (
        <div
            className={`flex min-h-[280px] flex-col items-center justify-center rounded-xl px-6 py-10 text-center ${
                serverError
                    ? "border border-slate-200 bg-slate-50"
                    : "border border-red-100 bg-red-50"
            }`}
        >
            <IconAlertCircle
                size={40}
                className={`mb-3 ${serverError ? "text-slate-500" : "text-red-500"}`}
            />
            <p className="text-base font-semibold text-gray-900">{copy.title}</p>
            <p className="mt-2 max-w-md text-sm text-gray-600">{copy.message}</p>
            {copy.detail ? (
                <p className="mt-1 max-w-md text-xs text-gray-500">{copy.detail}</p>
            ) : null}

            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                    type="button"
                    onClick={onRetry}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
                >
                    <IconRefresh size={16} />
                    {copy.actionLabel}
                </button>

                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                >
                    Close
                </button>
            </div>
        </div>
    );
};

export default FaceSearchFeedback;
